/** @file sampleNode: one node's local sample at a clip time (wrap targets, mirrorHalf twins, held fields). */
import type { Quat } from '@zilo/pose3d-math/quat/quat';
import { quatIdentity } from '@zilo/pose3d-math/quat/quatIdentity';
import { quatMirrorX } from '@zilo/pose3d-math/quat/quatMirrorX';
import { quatSlerp } from '@zilo/pose3d-math/quat/quatSlerp';
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import { vec3Lerp } from '@zilo/pose3d-math/vec3/vec3Lerp';
import type { ClipChannels, Key, NodeChannels } from '@zilo/pose3d-pose/sample/channelTypes';
import type { LocalSample } from '@zilo/pose3d-pose/sample/localTypes';
import { sampleKeys } from '@zilo/pose3d-pose/sample/sampleKeys';
import { sampleHeld } from '@zilo/pose3d-pose/sample/sampleHeld';
import type { ClipTime } from '@zilo/pose3d-pose/time/timeTypes';

const ZERO: Vec3 = [0, 0, 0];
const lerpNum = (a: number, b: number, t: number) => a + (b - a) * t;
const mirrorVec = (v: Vec3): Vec3 => [-v[0], v[1], v[2]];
const same = <T>(v: T): T => v;

/** How a field is sampled: its channel, rest value, interpolation and mirror. */
type Field<T> = { pick: (n: NodeChannels) => Key<T>[]; rest: T; lerp: (a: T, b: T, t: number) => T; mirror: (v: T) => T };

const NONE: NodeChannels = { rot: [], pos: [], scale: [], parent: [], hidden: [], rotToggle: [] };

const ROT: Field<Quat> = { pick: (n) => n.rot, rest: quatIdentity(), lerp: quatSlerp, mirror: quatMirrorX };
const POS: Field<Vec3> = { pick: (n) => n.pos, rest: ZERO, lerp: vec3Lerp, mirror: mirrorVec };
const SCALE: Field<number> = { pick: (n) => n.scale, rest: 1, lerp: lerpNum, mirror: same };

/**
 * Field value of node `src` at source frame `f` (decision 6): after its last key a loop runs toward the key at
 * `frames` if present (an ordinary key), else toward the `loopStart` value at `frames`, or with mirrorHalf toward the
 * mirrored twin value at `loopStart` placed at `loopStart + half`; a one-shot holds. Empty channel -> rest value.
 */
function fieldAt<T>(chs: ClipChannels, field: Field<T>, src: number, f: number): T {
  const ch = field.pick(chs.nodes[src] ?? NONE);
  if (ch.length === 0) return field.rest;
  const { clip } = chs;
  if (!clip.loop || chs.loopLen <= 0) return sampleKeys(ch, f, 0, null, field.lerp);
  if (chs.half > 0) {
    const twinCh = field.pick(chs.nodes[chs.rig.twin[src] ?? src] ?? NONE);
    const twinValue = twinCh.length === 0 ? field.rest : sampleKeys(twinCh, clip.loopStart, 0, null, field.lerp);
    return sampleKeys(ch, f, clip.loopStart + chs.half, field.mirror(twinValue), field.lerp);
  }
  return sampleKeys(ch, f, clip.frames, sampleKeys(ch, clip.loopStart, 0, null, field.lerp), field.lerp);
}

/**
 * Node `nodeIndex` at `time` into `out` (decisions 5, 6, 9, 18): in the mirrored half (`frame >= loopStart + half`) the
 * twin is sampled at `frame - half` and mirrored (rotation `quatMirrorX`, position x negated, parent mapped to its
 * twin); parent, hidden and rotToggle are held. Hidden defaults to the node's own track `visible` (inverted), else
 * the document's `hidden`; a held hidden key overrides both. `frameKey` is left to the caller.
 */
export function sampleNode(channels: ClipChannels, nodeIndex: number, time: ClipTime, out: LocalSample): LocalSample {
  const { clip, rig, half } = channels;
  const mirrored = half > 0 && time.frame >= clip.loopStart + half;
  const f = mirrored ? time.frame - half : time.frame;
  const src = mirrored ? (rig.twin[nodeIndex] ?? nodeIndex) : nodeIndex;
  const rot = fieldAt(channels, ROT, src, f);
  const pos = fieldAt(channels, POS, src, f);
  const r = mirrored ? quatMirrorX(rot) : rot;
  out.rot[0] = r[0]; out.rot[1] = r[1]; out.rot[2] = r[2]; out.rot[3] = r[3];
  out.pos[0] = mirrored ? -pos[0] : pos[0]; out.pos[1] = pos[1]; out.pos[2] = pos[2];
  out.scale = fieldAt(channels, SCALE, src, f);
  const node = channels.nodes[src] ?? NONE;
  const wrapped = clip.loop && time.wraps > 0;
  const parent = sampleHeld(node.parent, f, -1, wrapped, clip.loopStart);
  out.parent = mirrored && parent >= 0 ? (rig.twin[parent] ?? parent) : parent;
  const visible = clip.tracks?.[rig.names[nodeIndex] ?? '']?.visible;
  const hiddenDef = visible === undefined ? rig.hiddenDefault[nodeIndex] === 1 : !visible;
  out.hidden = sampleHeld(node.hidden, f, hiddenDef, wrapped, clip.loopStart);
  out.rotToggle = sampleHeld(node.rotToggle, f, false, wrapped, clip.loopStart);
  return out;
}
